import { AssignedTo, FolderDto } from "@green0meter/api-client";
import { Modal } from "@mui/material";
import React, { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { object, string, number, array } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@components/base";
import { ASSIGN_TO_VALUES } from "@type/User";
import { Chip } from "@components/common";
import { useGetUsers } from "@api";
import { useUserStore } from "@store/user";

type FolderModalMode = "create" | "edit";

interface CreateEditFolderModal {
  open: boolean;
  onClose: () => void;
  onConfirm?: (
    folderName: string,
    assignedTo: AssignedTo,
    parentFolderId: string,
    userIds: string[],
  ) => void;
  onConfirmEdit?: (
    folderId: string,
    folderName: string,
    assignedTo: AssignedTo,
    parentFolderId: string,
    userIds: string[],
  ) => void;
  isLoading?: boolean;
  folder?: FolderDto | null;
  rootFolder: FolderDto | null;
  mode?: FolderModalMode;
}

interface CreateEditFolderFormValues {
  folderName: string;
  parentFolderId: string;
  assignedTo: AssignedTo;
  userIds: string[];
}
const CreateEditFolderModal: FC<CreateEditFolderModal> = ({
  open,
  onClose,
  onConfirm,
  onConfirmEdit,
  isLoading = false,
  folder,
  rootFolder,
  mode = "create",
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { users } = useGetUsers(user, {
    companyId: user?.activeCompanyId,
  });

  const rootFolderOptions =
    rootFolder?.children
      ?.filter((child) => !child.isRoot && child.id !== folder?.id)
      .map((child) => ({
        id: child.id,
        name: child.name,
      })) ?? [];

  const assignedTos = Object.entries(ASSIGN_TO_VALUES).filter(
    ([id]) => Number(id) !== AssignedTo.Mixed,
  );

  const schema = object({
    folderName: string()
      .trim()
      .min(1, t("validation.required"))
      .required(t("validation.required")),
    parentFolderId: string().required(t("validation.required")),
    assignedTo: number().required(),
    userIds: array().of(string().required()).default([]),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<CreateEditFolderFormValues>({
    defaultValues: {
      folderName: mode === "edit" && folder ? folder.name : "",
      parentFolderId:
        mode === "edit" && folder
          ? folder.parentId ?? ""
          : folder?.id ?? rootFolder?.id ?? "",
      assignedTo: folder?.assignment?.assignedTo,
    },
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  useEffect(() => {
    // TODO: after roles assignment add  mode === "edit" && folder ? folder.assignedTo : assignedTo.Owner
    reset({
      folderName: mode === "edit" && folder ? folder.name : "",
      parentFolderId:
        mode === "edit" && folder ? folder.parentId ?? "" : rootFolder?.id ?? "",
      assignedTo:
        mode === "edit" && folder
          ? folder.assignment?.assignedTo
          : AssignedTo.OnlyOwner,
      userIds: Array.isArray(folder?.assignment?.userIds)
        ? folder.assignment.userIds
        : [],
    });
  }, [open, folder, reset, mode, rootFolder]);

  const onSubmit = (values: CreateEditFolderFormValues): void => {
    if (mode === "edit" && folder && onConfirmEdit) {
      onConfirmEdit(
        folder.id,
        values.folderName.trim(),
        values.assignedTo,
        values.parentFolderId,
        values.userIds,
      );
    } else if (mode === "create" && onConfirm) {
      onConfirm(
        values.folderName.trim(),
        values.assignedTo,
        values.parentFolderId,
        values.userIds,
      );
    }
    reset();
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        onClose();
        reset();
      }}
    >
      <Box
        p={4}
        maxWidth={400}
        mx="auto"
        my="20vh"
        borderRadius={2}
        boxShadow={3}
        bgcolor="background.paper"
      >
        <Typography variant="h6" mb={2}>
          {mode === "edit"
            ? t("createFolderModal.editTitle")
            : t("createFolderModal.title")}
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <Controller
            control={control}
            name="folderName"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("createFolderModal.folderName")}
                fullWidth
                disabled={isLoading}
                error={!!error}
                helperText={error?.message}
                autoFocus
                margin="normal"
              />
            )}
          />
          <Controller
            control={control}
            name="parentFolderId"
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth margin="normal" error={!!error}>
                <InputLabel>{t("createFolderModal.selectFolder")}</InputLabel>
                <Select
                  {...field}
                  label={t("createFolderModal.selectFolder")}
                  size="small"
                >
                  <MenuItem value={rootFolder?.id}>
                    {t("createFolderModal.noFolder")}
                  </MenuItem>
                  {rootFolderOptions.map((option) => (
                    <MenuItem value={option.id} key={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
                {error && (
                  <Typography color="error" variant="caption">
                    {error.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="userIds"
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth margin="normal" error={!!error}>
                <InputLabel>{t("createDocumentModal.selectUsers")}</InputLabel>
                <Select
                  {...field}
                  multiple
                  label={t("createDocumentModal.selectUsers")}
                  value={field.value ?? []}
                  onChange={(event) => field.onChange(event.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(selected as string[]).map((userId) => {
                        const user = users?.items.find((u) => u.id === userId);

                        // TODO: fix onDelete behavior
                        return (
                          <Chip
                            key={userId}
                            label={
                              user ? `${user.firstName} ${user.lastName}` : userId
                            }
                            size="small"
                            type={""}
                          />
                        );
                      })}
                    </Box>
                  )}
                  size="small"
                >
                  {users?.items.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </MenuItem>
                  ))}
                </Select>
                {error && (
                  <Typography color="error" variant="caption">
                    {error.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="assignedTo"
            render={({ field }) => (
              <FormControl fullWidth margin="normal">
                <InputLabel>{t("createFolderModal.assignTo")}</InputLabel>
                <Select
                  {...field}
                  label={t("createFolderModal.assignTo")}
                  size="small"
                  renderValue={(selected) => (
                    <Chip
                      label={t(`assignTo.${assignedTos[selected as AssignedTo][1]}`)}
                      type={selected as AssignedTo}
                      size="small"
                    />
                  )}
                >
                  {assignedTos.map(([id, key]) => (
                    <MenuItem value={Number(id)} key={id}>
                      <Chip
                        label={t(`assignTo.${key}`)}
                        type={Number(id)}
                        size="small"
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
            <Button variant="outlined" onClick={onClose} disabled={isLoading}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={!isValid || isLoading}
            >
              {mode === "edit" ? t("common.save") : t("common.create")}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default CreateEditFolderModal;
